import { useEffect, useRef } from "react";

import { authClient } from "~/lib/auth";
import { identifyPayload, shouldIdentify } from "~/lib/identify";
import { isImpersonationSession } from "~/lib/impersonation";
import { op } from "~/lib/openpanel";
import { analyticsEnabled } from "~/lib/track";

/**
 * Ties OpenPanel events to a profile. Mounted once at the app root and reactive
 * on the auth session: identifies a non-anonymous user (and re-identifies when
 * the id changes, e.g. anonymous -> verified), and calls `op.clear()` on logout.
 * Gated by `analyticsEnabled()` so it stays inert in dev/test and without a
 * client id, matching the manual `track()` wrapper. Skips support impersonation.
 */
export function useIdentify(): void {
  const { data: session } = authClient.useSession();
  const identifiedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!analyticsEnabled() || isImpersonationSession(session)) {
      if (identifiedIdRef.current) {
        op.clear();
        identifiedIdRef.current = null;
      }
      return;
    }

    if (shouldIdentify(session)) {
      const payload = identifyPayload(session);
      if (identifiedIdRef.current !== payload.profileId) {
        void op.identify(payload);
        identifiedIdRef.current = String(payload.profileId);
      }
      return;
    }

    if (identifiedIdRef.current) {
      op.clear();
      identifiedIdRef.current = null;
    }
  }, [session]);
}
